/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { offEvent, onEvent } from "@adapptio/lib-shared";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { ResourcePathBreadcrumbs } from "../../../../ui/components/ResourcePathBreadcrumbs";
import { Breadcrumbs } from "../../../../ui/components/Breadcrumbs/Breadcrumbs";
import { IBreadcrumbsItem } from "../../../../ui/components/Breadcrumbs/BreadcrumbsTypes";
import { ToolbarSpacer } from "../../../../ui/components/Toolbar/ToolbarSpacer";
import { ViewEditorView } from "../ViewEditorView";
import { isSelectionItemComponent } from "@adapptio/lib-components";

export interface IViewEditorSecondaryToolbarProps {
	view: ViewEditorView;
}

export const ViewEditorSecondaryToolbar: FC<IViewEditorSecondaryToolbarProps> = ({ view }) => {
	const editSelection = useMemo(() => view.getViewModel().getSelection(), [ view ]);
	const [ selectedItems, setSelectedItems ] = useState(editSelection.getItems());
	const selectedItem = selectedItems[0];

	const refreshSelectedItem = useCallback(() => {
		setSelectedItems(editSelection.getItems());
	}, [ editSelection, setSelectedItems ]);

	const [ resourceInfo, setResourceInfo ] = useState(() => ({
		resource: view.getResource(),
		parentList: view.getResourceParentList(),
		isBeingSaved: view.isBeingSaved()
	}));

	useEffect(() => {
		const handleResourceInfoChange = () =>
			setResourceInfo({
				resource: view.getResource(),
				parentList: view.getResourceParentList(),
				isBeingSaved: view.isBeingSaved()
			});

		const handleSelectionChange = () => {
			refreshSelectedItem();
		};

		onEvent(view.onResourceInfoChanged, handleResourceInfoChange);
		onEvent(editSelection.onChange, handleSelectionChange);
		return () => {
			offEvent(view.onResourceInfoChanged, handleResourceInfoChange);
			offEvent(editSelection.onChange, handleSelectionChange);
		};
	}, [ view, editSelection, refreshSelectedItem ]);

	// If a component is selected, show selection path breadcrumb for it
	const selectedComponentId =
		isSelectionItemComponent(selectedItem) && selectedItem?.cmpInstance?.id
			? selectedItem.cmpInstance.id
			: null;

	// Build selection path items for selected component
	const selectionPathItems: IBreadcrumbsItem[] = selectedComponentId
		? [ { label: selectedComponentId } ]
		: [];

	return (
		<>
			<ResourcePathBreadcrumbs
				parentList={resourceInfo.parentList}
				resourceName={resourceInfo.resource?.descriptor?.name}
				resourceId={resourceInfo.resource?.id}
				maxVisibleCrumbs={12}
			/>
			{selectionPathItems.length > 0 && (
				<>
					<ToolbarSpacer grow={1} />
					<Breadcrumbs items={selectionPathItems} maxVisibleCrumbs={12} />
				</>
			)}
		</>
	);
};

